import React, { createContext, useContext, useState, useEffect } from 'react';
import { remoteConfigService } from '../services/remoteConfigService';

export interface RemoteConfigContextType {
  enablePayment: boolean;
  requireSubscription: boolean;
  enableSignup: boolean;
  enableNewPalette: boolean;
  supportEmail: string;
  loginUrl: string;
  maxFileSizeMb: number;
  enableLanguages: boolean;
  clientPasswordMinLength: number;
  clientPasswordMinUppercase: number;
  clientPasswordMinLowercase: number;
  clientPasswordMinNumbers: number;
  clientPasswordMinSpecialChars: number;
}

const RemoteConfigContext = createContext<RemoteConfigContextType | undefined>(undefined);

export function RemoteConfigProvider({ children }: { children: React.ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [values, setValues] = useState<RemoteConfigContextType>({
    enablePayment: true,
    requireSubscription: true,
    enableSignup: true,
    enableNewPalette: false,
    supportEmail: 'kontakt@centrumcoucha.pl',
    loginUrl: '',
    maxFileSizeMb: 5,
    enableLanguages: true,
    clientPasswordMinLength: 10,
    clientPasswordMinUppercase: 1,
    clientPasswordMinLowercase: 1,
    clientPasswordMinNumbers: 1,
    clientPasswordMinSpecialChars: 1,
  });

  useEffect(() => {
    remoteConfigService.init()
      .then((cfg) => {
        setValues({
          enablePayment: cfg.enable_payment,
          requireSubscription: cfg.require_subscription,
          enableSignup: cfg.enable_signup,
          enableNewPalette: cfg.enable_new_pallete,
          supportEmail: cfg.support_email,
          loginUrl: cfg.login_url || '',
          maxFileSizeMb: cfg.max_file_size_mb,
          enableLanguages: cfg.enable_languages,
          clientPasswordMinLength: cfg.client_password_min_length,
          clientPasswordMinUppercase: cfg.client_password_min_uppercase,
          clientPasswordMinLowercase: cfg.client_password_min_lowercase,
          clientPasswordMinNumbers: cfg.client_password_min_numbers,
          clientPasswordMinSpecialChars: cfg.client_password_min_special_chars,
        });
      })
      .catch((err) => {
        console.error("❌ [RemoteConfigProvider] Błąd inicjalizacji konfiguracji:", err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  return (
    <RemoteConfigContext.Provider value={values}>
      {!loading && children}
    </RemoteConfigContext.Provider>
  );
}

export function useRemoteConfig() {
  const context = useContext(RemoteConfigContext);
  if (context === undefined) {
    throw new Error('useRemoteConfig must be used within a RemoteConfigProvider');
  }
  return context;
}
